import { useEffect, useRef } from "react";
import { getTinodeInstance, connectTinodeInstance } from "../services/tinodeSingleton";
import { subscribeToTopicWithMessages } from "../services/tinodeApi";

/**
 * Subscribe to consultation topic when customer is NOT on chat page,
 * and update customerChatUnreadCount when new messages from doctor arrive.
 * Used by PageTemplate for the chat bubble unread badge.
 */
export function useCustomerChatUnread(topicId, isOnChatPage, setCustomerChatUnreadCount) {
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (!topicId || isOnChatPage || !setCustomerChatUnreadCount) {
      return;
    }

    let isActive = true;
    let topic = null;

    const setup = async () => {
      try {
        const tinode = await getTinodeInstance();
        if (!isActive) return;
        await connectTinodeInstance(tinode);
        if (!isActive) return;
        if (!tinode.isAuthenticated()) return;

        topic = tinode.getTopic(topicId);
        if (!topic || !topic.isSubscribed()) {
          topic = await subscribeToTopicWithMessages(tinode, topicId, 1);
        }
        if (!isActive || !topic) return;

        const currentUserId = tinode.getCurrentUserID?.() || "";
        const seq = topic.seq != null ? topic.seq : 0;
        const read = topic.read != null ? topic.read : 0;
        const initialUnread = Math.max(0, Number(seq) - Number(read));
        if (initialUnread > 0) {
          setCustomerChatUnreadCount((prev) => Math.max(prev, initialUnread));
        }

        const onData = (msg) => {
          if (!msg || !msg.from) return;
          const from = typeof msg.from === "string" ? msg.from.trim() : String(msg.from || "");
          if (from && from !== currentUserId) {
            setCustomerChatUnreadCount((prev) => prev + 1);
          }
        };

        topic.onData = onData;
        cleanupRef.current = () => {
          if (topic) topic.onData = null;
        };
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[useCustomerChatUnread] setup failed:", err?.message || err);
        }
      }
    };

    setup();

    return () => {
      isActive = false;
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [topicId, isOnChatPage, setCustomerChatUnreadCount]);
}
